
import { ApiValidationError, ApiValidationErrorType, PensionCompany } from "../model";

import { Iban } from "./Iban";
import { Dates } from "./Dates";

/**
 * Provides base methods for validation.
 * These are typically used in user interface validator components.
 */
export class Validation {

  /**
   * Converts a validation error to a string that is in the same format as the messages in ApiList items.
   * Used in moving validatiaon errors to list items.
   */
  public static validationErrorAsMessage(error: ApiValidationError): string {
    if (!error) {
      return null;
    }
    let uiClass: string;
    let type = "UnknownValidationError";
    switch (error.type) {
      case ApiValidationErrorType.Warning:
        uiClass = "Warning";
        type = "ValidationWarning";
        break;
      case ApiValidationErrorType.General:
        uiClass = "Danger";
        type = "ValidationErrorGeneral";
        break;
      case ApiValidationErrorType.Invalid:
        uiClass = "Danger";
        type = "ValidationErrorInvalid";
        break;
      case ApiValidationErrorType.Required:
        uiClass = "Danger";
        type = "ValidationErrorRequired";
        break;
      default:
        uiClass = "Danger";
        break;
    }
    return `${type} (Ui${uiClass}): ${error.msg} (ANON at ${Dates.getToday()} 00:00)`;
  }

  /**
   * Returns true if the given value is a valid Finnish Personal ID (HETU / SSN).
   * Checks the format and checksum.
   * @param value Candidate for Finnish Personal ID.
   */
  public static isPersonalIdFi(value: string): boolean {

    if (!value) {
      return true;
    }

    const pattern = /^(\d{6})([+\-ABCDEFUVWXY])(\d{3})([a-zA-Z0-9*])$/;
    const groups = pattern.exec(value);

    if (!groups) {
      return false;
    }

    const date = groups[1];
    const centenry = groups[2];
    const person = groups[3];
    const checksum = groups[4];

    const cc =  ["A","B","C","D","E","F"].includes(centenry) ? "20" :
    ["-", "U", "V", "W", "X", "Y" ].includes(centenry)  ? "19" :
    ["+" ].includes(centenry) ? "18" : null;

    if (cc === null) {
      return false;
    }

    if (date[0] !== "4") {
      // Check date if not artificial ones beginning with 4
      if (!Dates.isValidDateTime(date.substr(0, 4) + cc + date.substr(4, 2), true, "DDMMYYYY")) {
        return false;
      }
    }
    else {
       // Check date if artificial ones beginning with 4
      if (!Dates.isValidDateTime("0101" + cc + date.substr(4, 2), true, "DDMMYYYY")) {
        return false;
      }
    }

    if (checksum === "*") {
      return true;
    }

    const checksumLetters = "0123456789ABCDEFHJKLMNPRSTUVWXY";
    const calculatedChecksum = checksumLetters[parseInt(date + person, 10) % 31];
    return calculatedChecksum === checksum.toUpperCase();
  }

  /**
   * Returns true if the value is a valid e-mail.
   * Currently uses a simple regex (source?).
   * @param value E-mail candidate.
   */
  public static isEmail(value: string): boolean {
    // TODO: The escape is probably useless, but needs testing
    // eslint-disable-next-line no-useless-escape
    const regExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regExp.test(value);
  }

  /**
   * Returns true if the value is a valid mobile phone number: 5-16 numbers and spaces (5-18 if there is a "+" in the beginning).
   * Currently uses a naive regex, but we could extend this to take into account the regional differences.
   * @param value Mobile phone number candidate.
   */
  public static isMobilePhone(value: string): boolean {
    // TODO: Check lengths.
    const regExp = /^(\+[\d ]{1,3}[\d ]{4,15})$|^([\d ]{5,16})$/;
    return regExp.test(value);
  }

  /**
   * Returns true if the value is a valid currency amount with maximum two decimals.
   * @param value Currency amount candidate
   */
  public static isCurrency(value: string): boolean {
    const regExp = /^\d+(([.]|[,])\d{1,2})?$/;
    return regExp.test(value);
  }

  /**
   * Returns true if the value is a valid number.
   * @param value Number candidate
   */
  public static isNumber(value: string): boolean {
    const regExp = /^\d+(([.]|[,])\d+)?$/;
    return regExp.test(value);
  }

  /**
   * Returns true if the value is a valid integer.
   * @param value Number candidate
   */
  public static isInteger(value: string): boolean {
    const regExp = /^\d+$/;
    return regExp.test(value);
  }

  /**
   * Returns true if the value is a valid tax percent amount with maximum one decimal.
   * @param value Tax percent candidate
   */
  public static isTaxPercent(value: string): boolean {
    const regExp = /^\d{1,2}(([.]|[,])\d{1})?$/;
    return regExp.test(value);
  }

  /** Returns true if the value is a valid Finnish Postal code: 5 numbers. */
  public static isPostalCodeFi(value: string): boolean {
    const regExp = /^\d{5}$/;
    return regExp.test(value);
  }

  /**
   * Returns true if the value is a valid Finnish Company ID. If the format is not correct, will return false.
   * @param companyIdCandidate String to validate / format
   * @param allowStarAsChecksum If true, allows the checksum character (the last char) to be an asterix (*).
   * @returns boolean
   */
  public static formatCompanyIdFi(companyIdCandidate: string, allowStarAsChecksum = true): boolean {
    if (!companyIdCandidate) {
      return false;
    }
    companyIdCandidate = companyIdCandidate.toUpperCase().trim().replace("-", "").replace("–", "").replace("—", ""); // dash, n-dash, m-dash
    if (companyIdCandidate.substr(0, 2) === "FI") {
      companyIdCandidate = companyIdCandidate.substr(2);
    }
    if (companyIdCandidate.length === 7) {
      // Some old companies may have 6+1 numbers
      companyIdCandidate = "0" + companyIdCandidate;
    }
    if (companyIdCandidate.length !== 8) {
      return false;
    }
    const multipliers = [7, 9, 10, 5, 8, 4, 2];
    let total = 0;
    for (let i = 0; i < 7; i++) {
      const num = Number(companyIdCandidate.charAt(i));
      if (num < 0 || num > 9) {
        return false;
      }
      total += num * multipliers[i];
    }
    const modulo = total % 11;
    if (modulo === 1) {
      return false;
    }
    const checksum = modulo === 0 ? 0 : (11 - modulo);
    return (allowStarAsChecksum && companyIdCandidate.charAt(7) === "*") || Number(companyIdCandidate[7]) === checksum;
  }

  /**
   * Returns true if the value is a valid IBAN number.
   * Checks for the country specific length as well as checksum.
   * @param value IBAN number number candidate.
   */
  public static isIban(value: string): boolean {
    return Iban.isIban(value);
  }

  /**
   * For validating (RF) reference number.
   * @param value - (RF) reference number.
   *
   * In Finland, the RF reference is formed on the basis of the Finnish reference number. Structure of the RF reference is as follows:
   * RFXX1234561, in which
   * RF is the identifier
   * XX are the two check digits
   * 1234561 is the Finnish reference number, which can be freely formed by the customer.
   * Maximum length of the number is 20 digits, and the final number is a check digit calculated using the algorithm for Finnish reference numbers.
   * The Finnish reference number is appended with the numbers "2715" (the letters RF converted into integers) and "00"
   * The modulo 97 operation is performed on the resulting figure.
   * The remainder is then deducted from 98, which then results in the checksum. If the checksum is smaller than 10, a leading zero is added.
   */
   public static isFinnishBankReferenceNumber(value: string): boolean {

    if (value == null) {
      return false;
    }
    // Remove spaces
    value = value.split(" ").join("");

    // Check if given value is RF reference number
    const isRf = value.toUpperCase().startsWith("RF");

    // If number is RF reference number
    if(isRf){
      // The two digit RF checksum
      const checksum = value.slice(2, 4);

      // Referencenumber
      let referenceNumber = value.slice(4);
      // remove leading zeros
      referenceNumber = referenceNumber.replace(/^0+/, "");

      const referenceNumberWithoutCheck =  referenceNumber.substring(0, referenceNumber.length - 1);
      const referenceCheck = parseInt(referenceNumber.slice(-1));
      // Move the first two characters (RF = 2715) to the end of the string and add 00
      const rearrangedValue = value.slice(4)  + "271500";
      // to number
      let num: bigint;
      try {
        num = BigInt(rearrangedValue);
      } catch (error) {
        console.error(error);
        return false;
      }
      let calculatedChecksum = Number(num % 97n);
      calculatedChecksum = 98 - calculatedChecksum;

      let calculatedChecksumString = calculatedChecksum.toString();

      if (calculatedChecksum < 10){
        calculatedChecksumString = calculatedChecksumString.padStart(2, "0");
      }
      // Does RF checksum and reference checksum match the calculated values
      if(calculatedChecksumString == checksum && Validation.getCheckNumber(referenceNumberWithoutCheck) == referenceCheck){
        return true;
      }else{
        return false;
      }

    }else if(!isRf){
      // Remove leading zeros
      const referenceNumber = value.replace(/^0+/, "");

      const referenceNumberWithoutCheck =  referenceNumber.substring(0, referenceNumber.length - 1);
      const referenceCheck = parseInt(referenceNumber.slice(-1));

      // Given value is not a RF reference number. Check if reference number valid.
      if(Validation.getCheckNumber(referenceNumberWithoutCheck) == referenceCheck){
        return true;
      }else{
        return false;
      }
    }
  }

  /**
   * For calculating bank reference checksum number.
   * @param value - Reference number without checksum.
   *
   * The reference number must always include a checksum. The checksum is calculated as follows:
   * All digits of the reference number are multiplied from right to left with the values 7, 3, 1.
   * The products are then summed up and the sum is subtracted from the following full ten.
   * The resulting difference is the checksum, the final digit in the reference number.
   * If the difference is 10, the checksum is 0.
   */
  public static getCheckNumber(value: string): number | undefined{

    const multipliers = [7, 3, 1];
    const reverse = value.split("").reverse();
    if (reverse.length > 19)
    {
      return undefined;
    }
    let sum = 0;

    for (let i = 0; i < reverse.length; i++) {
      const num = parseInt(reverse[i]);
      sum += num * multipliers[i % 3];
    }
    return ((10 - (sum % 10)) % 10);
  }

  /**
   * Returns PensionCompany for the given pension contract number.
   * @param value - Pension contract number.
   */
  public static isPensionContractNumber(value: string): PensionCompany {

    if (value == null) {
      return null;
    }

    let regExp: RegExp = null;

    // TODO: The escape is probably useless, but needs testing
    if (value.startsWith("7") || value.startsWith("8")) {
      // eslint-disable-next-line no-useless-escape
      regExp = /^(\d{5})\-?(\d{4})([a-zA-Z0-9\*])$/;
    } else {
      // eslint-disable-next-line no-useless-escape
      regExp = /^(\d{2})\-?(\d{7})([a-zA-Z0-9\*])$/;
    }
    const result = regExp.exec(value);
    if (!result) {
      return null;
    }

    const company = result[1];
    const contract = result[2];
    const check = result[3];

    const checksumLetters = "0123456789ABCDEFHJKLMNPRSTUVWXY";
    const calculatedChecksum = checksumLetters[parseInt(company + contract, 10) % 31];
    if (calculatedChecksum !== check) {
      return null;
    }
    switch (company) {
      case "80009":
        return PensionCompany.Apteekkien;
      case "80008":
        return PensionCompany.Verso;
      case "54":
        return PensionCompany.Elo;
      case "90":
        return PensionCompany.Etera;
      case "46":
        if(contract?.startsWith("4")){
          // This is Yel
          return null;
        }
        return PensionCompany.Ilmarinen;
      case "40":
        return PensionCompany.PensionsAlandia;
      case "55":
        if(contract?.startsWith("2")){
          // This is Yel
          return null;
        }
        return PensionCompany.Varma;
      case "56":
        return PensionCompany.Veritas;
      case "20":
        return PensionCompany.KevaMunicipal;
      case "24":
        return PensionCompany.KevaAland;
      case "25":
        return PensionCompany.KevaChurch;
      case "29":
        return PensionCompany.KevaKela;
      case "30":
        return PensionCompany.KevaState;
      case "80001":
        return PensionCompany.Valio;
    }
    return null;
  }

  /**
   * Returns PensionCompany for the given *temporary* pension contract number.
   * @param value - TemporaryPension contract number.
   */
  public static isTemporaryPensionContractNumber(value: string): PensionCompany {

    if (value == null) {
      return null;
    }

    switch (value) {
      case "55-3999990D":
        return PensionCompany.Varma;
      case "46-3000000V":
        return PensionCompany.Ilmarinen;
      case "54-0000000U":
        return PensionCompany.Elo;
      case "56-0049990P":
        return PensionCompany.Veritas;
    }
    return null;
  }
}
